import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminClinicService } from '../services/admin/admin-clinic.service';
import { CreateClinicRequest } from '../common/model/dto/create-clinic-request';
import { CreateDemoAccountRequest } from '../common/model/dto/create-demo-account-request';
import { AdminClinicLocationService } from '../services/admin/admin-clinic-location.service';
import { Clinic } from '../common/model/dto/clinic';
import { CreateClinicLocationRequest } from '../common/model/dto/create-clinic-location-request';

export function useAdminClinicListMutations() {
  const qc = useQueryClient();

  const invalidateClinicQuery = () =>
    qc.invalidateQueries({ queryKey: ['admin-clinics'] });

  const invalidateClinicQueryById = (clinicId: string) =>
    qc.invalidateQueries({ queryKey: ['admin-clinic', clinicId] });

  const createClinic = useMutation({
    mutationFn: (createClinicRequest: CreateClinicRequest) =>
      AdminClinicService.createClinic(createClinicRequest),
    onSuccess: invalidateClinicQuery,
  });

  const createDemoClinic = useMutation({
    mutationFn: (createDemoClinicRequest: CreateDemoAccountRequest) =>
      AdminClinicLocationService.createDemoClinicLocation(
        createDemoClinicRequest
      ),
    onSuccess: invalidateClinicQuery,
  });

  const createClinicLocation = useMutation({
    mutationFn: ({
      clinicId,
      request,
    }: {
      clinicId: string;
      request: CreateClinicLocationRequest;
    }) => AdminClinicService.createClinicLocation(clinicId, request),
    onSuccess: () => {
      invalidateClinicQuery();
      qc.invalidateQueries({ queryKey: ['admin-clinic-locations'] });
    },
  });

  const updateClinic = useMutation({
    mutationFn: async ({
      clinicId,
      updatedProperties,
    }: {
      clinicId: string;
      updatedProperties: Partial<Clinic>;
    }) => {
      await AdminClinicService.updateClinic(clinicId, updatedProperties);

      return clinicId;
    },
    onSuccess: (clinicId: string) => invalidateClinicQueryById(clinicId),
  });

  const deleteClinic = useMutation({
    mutationFn: (clinicId: string) => AdminClinicService.deleteClinic(clinicId),
    onSuccess: (_, clinicId) => {
      qc.removeQueries({ queryKey: ['admin-clinic', clinicId] });
      invalidateClinicQuery();
    },
  });

  return {
    createClinic,
    createDemoClinic,
    updateClinic,
    deleteClinic,
    createClinicLocation,
  };
}
